import { useCallback, useRef } from 'react';
import { useBlocker } from '@tanstack/react-router';

// Holds an in-app navigation while a form has unsaved edits, so the caller can
// ask whether to discard them, and has the browser ask the same on a reload or
// tab close. The router's blocker owns both: it registers the `beforeunload`
// listener itself and consults `enableBeforeUnload` when the event fires.
export interface UnsavedChangesGuard {
  readonly dialogOpen: boolean;
  readonly proceed: () => void;
  readonly reset: () => void;
  // For a navigation the form starts itself after saving (a redirect to the
  // saved record), which must not be held by the edits it just saved.
  readonly allowNextNavigation: () => void;
}

export interface UseUnsavedChangesGuardOpts {
  readonly dirty: boolean;
}

export function useUnsavedChangesGuard({ dirty }: UseUnsavedChangesGuardOpts): UnsavedChangesGuard {
  const bypassRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => {
      if (bypassRef.current) {
        bypassRef.current = false;
        return false;
      }
      return dirty;
    },
    enableBeforeUnload: () => dirty && !bypassRef.current,
    withResolver: true,
  });

  const allowNextNavigation = useCallback(() => {
    bypassRef.current = true;
  }, []);

  return {
    dialogOpen: blocker.status === 'blocked',
    proceed: blocker.status === 'blocked' ? blocker.proceed : noop,
    reset: blocker.status === 'blocked' ? blocker.reset : noop,
    allowNextNavigation,
  };
}

function noop() {}
